import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useToasts } from '../../../contexts/toasts';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { RagLoader } from '../../../models/LLM';
import { Alert, Badge, Button, Card, Col, Container, Modal, Row } from 'react-bootstrap';
import AppSearch from '../../../components/common/AppSearch';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { formatDate } from '../../../utils';
import { TailSpin } from 'react-loader-spinner';
import EditLoaderModal from '../../../components/generics/EditLoaderModal';
import { Llm } from '../../../api/Llm';
import { Tooltip } from 'antd';
import UserItem from '../../../components/common/UserItem';
import { KrakendCollection } from '../../../models/generics';


export default function LoadersList() {
  const [loaders, setLoaders] = useState<RagLoader[]>();
  const [searchText, setSearchText] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState<number>();
  const [loader, setLoader] = useState<RagLoader>(new RagLoader());

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const filteredLoaders = useMemo(() => loaders?.filter(l => l.name.toLowerCase().includes(searchText.toLowerCase())), [loaders, searchText]);

  const initLoaders = useCallback(() => {
    Llm.getLoaders()
      .then(
        ({ data }) => setLoaders((data as KrakendCollection<RagLoader>).collection),
        (err) => {
          setLoaders([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loaders-error', message, `Loaders Error ${status ? `(${status})` : ''}`, 'danger');
        });
  }, [showToast]);

  useEffect(() => initLoaders(), [initLoaders]);

  const openCreateModal = useCallback(() => setShowEditModal(true), []);

  const reset = useCallback(() => {
    setLoaders(undefined);
    initLoaders();
  }, [initLoaders]);

  const handleClose = useCallback(() => {
    setShowEditModal(false)
    setShowDeleteModal(false);
    setLoader(new RagLoader());
    setEditing(undefined);
  }, []);

  const handleSubmit = useCallback((loader: RagLoader) => {
    showLoader();

    const body: RagLoader = {
      ...loader,
      config: loader.config!.map(c => {
        if (c.required && c.value === undefined) c.value = c.default;
        return c;
      }),
    }

    const promise = editing !== undefined
      ? Llm.editLoader(body)
      : Llm.createLoader(body);

    promise.then(
      () => {
        hideLoader();
        showToast('loader-success', `Loader ${editing !== undefined ? 'Updated' : 'Created'} successfully`, 'Loader Success', 'success');
        handleClose();
        reset();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('loaders-error', message, `Loaders Error ${status ? `(${status})` : ''}`, 'danger');
      });
  }, [showLoader, hideLoader, showToast, editing, reset, handleClose]);

  const deleteLoader = useCallback(() => {
    showLoader();

    Llm.deleteLoader(loader!.id as string)
      .then(
        ({ data }) => {
          hideLoader();
          handleClose();
          showToast('loaders-success', 'Loader deleted successfully', 'Loaders Success', 'success');
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loaders-error', message, `Loaders Error ${status ? `(${status})` : ''}`, 'danger');
        });
  }, [showLoader, hideLoader, handleClose, loader, reset, showToast]);

  const handleSearchChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(evt.target.value);
  }, []);

  const debounceSearchChange = useMemo(() => debounce(handleSearchChange, 500), [handleSearchChange]);

  const testLoader = useCallback((loader: RagLoader) => () => {
    showLoader();

    const body = {
      name: loader.type!,
      config: loader.config!.reduce<Record<string, any>>((acc, curr) => {
        if (curr.value !== undefined) acc[curr.name] = curr.value;
        return acc;
      }, {}),
    };

    Llm.testLoader(body)
      .then(
        ({ data }) => {
          hideLoader();
          data.is_working
            ? showToast('loader-test-success', 'Loader is working correctly', 'Loader Tested!', 'success')
            : showToast('loader-test-error', data.error, 'Loader Test Error', 'danger');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loader-test-error', message, `Loaders Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showLoader, hideLoader, showToast]);

  const handleLoaderEdit = useCallback((loader: RagLoader, index: number) => () => {
    setEditing(index);
    setLoader(loader);
    setShowEditModal(true);
  }, []);

  const handleLoaderDelete = useCallback((rag: RagLoader) => () => {
    setLoader(rag);
    setShowDeleteModal(true);
  }, []);

  const renderLoaders = useCallback(() => {
    return (
      <Row>
        {filteredLoaders?.map((loader, index) => (
          <Col key={loader.type} md={3} className="px-2">
            <Card className="shadow-sm mb-3">
              <div className="d-flex justify-content-between p-1">
                <Badge className="shadow w-auto">{loader.accessLevel}</Badge>
                <Badge bg="none" className="bg-pink shadow w-auto">{loader.type}</Badge>
              </div>
              <Card.Body className="py-2">
                <Card.Title className="mb-1">{loader.name}</Card.Title>
                <p className="line-clamp-2 text-secondary mb-2">{loader.description}</p>

                <div className="d-flex mb-3">
                  {loader.tags.length === 0 && <Badge className="bg-secondary" pill={true}>0 tags</Badge>}
                  {loader.tags.map((tag, index) => (
                    <Badge key={index} className="me-1" pill={true}>
                      <FontAwesomeIcon icon={solid('tag')} />
                      <span className="ms-2">{tag}</span>
                    </Badge>
                  ))}
                </div>

                <Row className="justify-content-center">
                  <Tooltip title="Test Loader">
                    <button className="circle-button dark" onClick={testLoader(loader)}>
                      <FontAwesomeIcon icon={solid('cogs')} />
                    </button>
                  </Tooltip>
                  <Tooltip title="Edit Loader">
                    <button className="circle-button dark ms-2" onClick={handleLoaderEdit(loader, index)}>
                      <FontAwesomeIcon icon={solid('pen')} />
                    </button>
                  </Tooltip>
                  <Tooltip title="Delete Loader">
                    <button className="circle-button dark ms-2" onClick={handleLoaderDelete(loader)}>
                      <FontAwesomeIcon icon={solid('trash')} />
                    </button>
                  </Tooltip>
                </Row>
              </Card.Body>
              <UserItem className="border rounded p-1 m-2" user={loader.owner!} organization={loader.organization!} />
              <small className="ms-1 font-sm text-muted">
                <b>Created:</b>
                <span className="ms-1">{formatDate(loader.createDateTime!)}</span>
              </small>
            </Card>
          </Col>
        ))}
      </Row>
    )
  }, [filteredLoaders, testLoader, handleLoaderEdit, handleLoaderDelete]);

  const renderAsyncContent = useCallback(() => {
    if (!filteredLoaders) return (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin
          height={100}
          width={100}
          color="#677ab1"
          radius="1"
        />
      </div>
    )

    return filteredLoaders.length === 0
      ? (
        <Alert className="py-2" variant="info">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No loaders found.</span>
        </Alert>
      )
      : renderLoaders();
  }, [filteredLoaders, renderLoaders]);

  return (
    <Container className="px-0" fluid>
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0">Loaders List</h4>
        </Col>

        <Col>
          <AppSearch placeholder="Search loaders..." onChange={debounceSearchChange} />
        </Col>

        <Col xs={6} md={4} className="text-end">
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create Loader</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <EditLoaderModal loader={loader} open={showEditModal} onClose={handleClose} onSubmit={handleSubmit} />

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Loader</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete loader <b>{loader?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteLoader}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}